/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { useState, useEffect, useCallback } from "react";
import { convertFromHTML, EditorState, ContentState } from "draft-js";
import { convertToHTML } from "draft-convert";

export function moveFocusToEnd(inputEditorState: EditorState) {
  return EditorState.moveSelectionToEnd(inputEditorState);
}

export function useSkillStepCard({ skillSessionStepRole, onEdit }) {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [editorContentLength, setEditorContentLength] = useState(0);
  const [showDeleteDetailPrompt, setShowDeleteDetailPrompt] = useState(false);

  useEffect(() => {
    if (skillSessionStepRole.detailContent) {
      const blocksFromHTML = convertFromHTML(
        skillSessionStepRole.detailContent
      );
      setEditorContentLength(
        blocksFromHTML["contentBlocks"].reduce(
          (acc, blocks) => acc + blocks.getLength(),
          0
        )
      );
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks
      );
      setEditorState(
        moveFocusToEnd(EditorState.createWithContent(contentState))
      );
    } else {
      setEditorState(EditorState.createEmpty());
    }
  }, [skillSessionStepRole.id]);

  const _handleChangeHeading = useCallback(
    (text) => {
      if (text !== skillSessionStepRole.heading) {
        const newSkillSessionStepRole = Object.assign({}, skillSessionStepRole);
        newSkillSessionStepRole.heading = text;
        onEdit(newSkillSessionStepRole);
      }
    },
    [skillSessionStepRole, onEdit]
  );

  const _handleChangeDetailTitle = useCallback(
    (text) => {
      if (text !== skillSessionStepRole.detailTitle) {
        const newSkillSessionStepRole = Object.assign({}, skillSessionStepRole);
        newSkillSessionStepRole.detailTitle = text;
        onEdit(newSkillSessionStepRole);
      }
    },
    [skillSessionStepRole, onEdit]
  );

  const _handleChangeDetailContent = useCallback(
    (newEditorState) => {
      const newContent = newEditorState.getCurrentContent();
      if (editorState.getCurrentContent() !== newContent) {
        const newSkillSessionStepRole = Object.assign({}, skillSessionStepRole);
        newSkillSessionStepRole.detailContent = convertToHTML(newContent);
        setEditorContentLength(
          convertFromHTML(newSkillSessionStepRole.detailContent)[
            "contentBlocks"
          ].reduce((acc, blocks) => acc + blocks.getLength(), 0)
        );
        onEdit(newSkillSessionStepRole);
      }
      setEditorState(newEditorState);
    },
    [skillSessionStepRole, onEdit, editorState]
  );

  const _handleToggleDetail = useCallback(() => {
    const newSkillSessionStepRole = Object.assign({}, skillSessionStepRole);
    if (skillSessionStepRole.detailContent) {
      setShowDeleteDetailPrompt((formerState) => !formerState);
    } else {
      newSkillSessionStepRole.detailTitle = "Detail";
      newSkillSessionStepRole.detailContent =
        "<ul><li>Here are some more details about this step.</li></ul>";
      const blocksFromHTML = convertFromHTML(
        newSkillSessionStepRole.detailContent
      );
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks
      );
      const newEditorState = EditorState.createWithContent(contentState);
      setEditorState(newEditorState);
      setEditorContentLength(
        convertFromHTML(newSkillSessionStepRole.detailContent)[
          "contentBlocks"
        ].reduce((acc, blocks) => acc + blocks.getLength(), 0)
      );
      onEdit(newSkillSessionStepRole);
    }
  }, [skillSessionStepRole, onEdit]);

  const _handleDeleteDetail = useCallback(() => {
    setShowDeleteDetailPrompt(false);
    const newSkillSessionStepRole = Object.assign({}, skillSessionStepRole);
    delete newSkillSessionStepRole.detailTitle;
    delete newSkillSessionStepRole.detailContent;
    onEdit(newSkillSessionStepRole);
  }, [skillSessionStepRole, onEdit]);

  const _handleKeepButton = () => {
    setShowDeleteDetailPrompt(false);
  };

  return {
    _handleChangeHeading,
    _handleChangeDetailTitle,
    _handleChangeDetailContent,
    _handleToggleDetail,
    _handleDeleteDetail,
    _handleKeepButton,
    editorState,
    editorContentLength,
    showDeleteDetailPrompt,
  };
}
